import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import { handleError } from 'api/query-client';
import { isInTemplateRoute } from 'ui/router';

import {
  TravellerAttributeTemplateData,
  createTravellerAttributeTemplate,
  deleteTravellerAttributeTemplate,
  duplicateTravellerAttributeTemplate,
  loadCompanyTravellerAttributeTemplate,
  loadCompanyTravellerAttributeTemplateVariables,
  loadCompanyTravellerAttributeTemplates,
  loadTemplateTravellerAttributes,
  updateTravellerAttributeTemplate,
  updateTravellerAttributeTemplateOrders,
} from './requests';

export const useCompanyTravellerAttributeTemplates = ({
  queryData,
  systemAttributes,
  journeyShortId,
  ...rest
}: {
  queryData?: object;
  systemAttributes?: boolean;
  journeyShortId?: string;
  staleTime?: number;
  keepPreviousData?: boolean;
  enabled?: boolean;
} = {}) => {
  const queryString = toQueryString(queryData ?? {});
  const isTemplate = isInTemplateRoute();
  return useQuery({
    keepPreviousData: true,
    queryKey: ['traveller-attributes', queryString, systemAttributes, journeyShortId],
    queryFn: () =>
      isTemplate && journeyShortId
        ? loadTemplateTravellerAttributes(journeyShortId, queryString)
        : loadCompanyTravellerAttributeTemplates(queryString, systemAttributes),
    ...rest,
  });
};

export const useCompanyTravellerAttributeTemplate = (
  shortId: string,
  isSystemAttributeQuery?: boolean
) => {
  return useQuery({
    queryKey: ['traveller-attributes', shortId],
    queryFn: () => loadCompanyTravellerAttributeTemplate(shortId, isSystemAttributeQuery),
    enabled: Boolean(shortId),
  });
};

export const useCompanyTravellerAttributeTemplateVariables = () => {
  return useQuery({
    queryKey: ['traveller-attribute-variables'],
    queryFn: () => loadCompanyTravellerAttributeTemplateVariables(),
  });
};

export const useCreateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTravellerAttributeTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeTemplateCreated'));
    },
  });
};

export const useUpdateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, shortId }: { data: TravellerAttributeTemplateData; shortId: string }) =>
      updateTravellerAttributeTemplate(shortId, data),
    onSuccess: ({ shortId }) => {
      queryClient.removeQueries(['traveller-attributes', shortId]);
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeEdited'));
    },
  });
};

export const useDuplicateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => duplicateTravellerAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeTemplateDuplicated'));
    },
  });
};

export const useUpdateTravellerAttributeTemplateOrders = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, number>) => updateTravellerAttributeTemplateOrders(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
    },
    onError: () => {
      handleError(t('attributeOrdersNotSaved'), () =>
        queryClient.invalidateQueries(['traveller-attributes'])
      );
    },
  });
};

export const useDeleteTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => deleteTravellerAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeDeleted'));
    },
  });
};

export const locale = {
  en: {
    attributeTemplateCreated: 'Field created',
    attributeTemplateDuplicated: 'Field duplicated',
    attributeEdited: 'Field edited',
    attributeDeleted: 'Field deleted',
    attributeOrdersNotSaved: 'Field order could not be saved',
  },
};
